import {
  INBOARD,
  OUTBOARD,
  NON_POWERED,
  PONTOON,
  SINGLE,
  MODIFIED_V,
  SEMI_V,
  TRI_HULL,
  V
} from 'constants/boatApplication';
import { _initBoatSystems } from './BaseBoat';
import moment from 'moment';

const initState = {
  id: 'new',
  boatType: PONTOON,
  status: 'DRAFT',
  engineType: null,
  boatSpecifications: {},
  boatSystems: [],
  cubicCapacity: {
    tubes: {
      'PORT': {},
      'CENTER': {},
      'STARBOARD': {},
    },
    invalidCells: {
      'PORT': {},
      'CENTER': {},
      'STARBOARD': {},
    },
    hasCenterTube: false
  },
  floatationCapacity: {},
  additionalFiles: [],
};

export default class Pontoon {
  constructor(boat = initState) {
    this.id = boat.id || initState.id;
    this.boatType = PONTOON;
    this.status = boat.status || initState.status;
    this.engineType = boat.engineType;
    this.plateType = boat.plateType;
    this.boatSpecifications = _initBoatSpecifications.call(this, boat.boatSpecifications || boat);
    this.boatSystems = _initBoatSystems(boat.boatSystems);
    this.specificationsRequiredFieldsList = getSpecificationsRequiredFieldsList.call(this);
    this.cubicCapacity = _initHullCalculations(boat);
    this.personPowerCapacity = _initPersonPowerCapacity.call(this, boat.personPowerCapacity ? {...boat.personPowerCapacity, dualSteering:boat.dualSteering} : boat);
    this.powerCapacity = _initPowerCapacity(boat.powerCapacity || boat);
    this.floatationCapacity = _initFloatationCapacity(boat.floatationCapacity);
    this.additionalFiles = boat.additionalFiles ? [...boat.additionalFiles] : initState.additionalFiles;
    this.isFloatationTestReportRequired = boat.boatSpecifications ? boat.boatSpecifications.lengthFeet < 20 : boat.lengthFeet < 20;
    this.bottomContourList =
      [
        {name: 'Modified V', value: MODIFIED_V},
        {name: 'Semi V', value: SEMI_V},
        {name: 'Tri Hull', value: TRI_HULL},
        {name: 'V', value: V},
      ];
    this.engineTypesList =
      [
        INBOARD,
        OUTBOARD,
        NON_POWERED
      ];
    this.engineSubTypesList =
      [
        {name: 'Up to 6 HP.', value: 'SUB_TYPE_1'},
        {name: 'Over 6 HP. and up to 20 HP.', value: 'SUB_TYPE_2'},
        {name: 'Over 20 HP. and up to 100 HP.', value: 'SUB_TYPE_3'},
        {name: 'Over 100 HP.', value: 'SUB_TYPE_4'},
      ];
  }
}

function getSpecificationsRequiredFieldsList() {
  let requiredFieldsList =
    [
      'name',
      'year',
      'cityAndStateForCapacityTag',
      'weightOfFinishedBoat',
      'fuelTankCapacity',
    ];
  if (this.engineType === INBOARD) requiredFieldsList.push('engineAndDriveWeight');
  if (this.engineType !== INBOARD) requiredFieldsList.push('transformHeightAtCenterline');
  return requiredFieldsList;
}

function _initBoatSpecifications(boat) {
  let boatSpecifications = {
    name: boat.name || '',
    year: boat.year || '',
    cityAndStateForCapacityTag: boat.cityAndStateForCapacityTag || '',
    fuelTankCapacity: boat.fuelTankCapacity === undefined ? '' : boat.fuelTankCapacity,
    weightOfFinishedBoat: boat.weightOfFinishedBoat === undefined ? '' : boat.weightOfFinishedBoat,
  };
  if (this.engineType === OUTBOARD) {
    boatSpecifications.motorType = boat.motorType || SINGLE;
    boatSpecifications.hasElectricPropulsion = !!boat.hasElectricPropulsion;
  }
  if (this.engineType === INBOARD) {
    boatSpecifications.engineAndDriveWeight = boat.engineAndDriveWeight === undefined ? '' : boat.engineAndDriveWeight;
  }
  if (this.engineType !== INBOARD) {
    boatSpecifications.transformHeightAtCenterline = boat.transformHeightAtCenterline === undefined ? '' : boat.transformHeightAtCenterline;
  }
  return boatSpecifications;
}

function _initPersonPowerCapacity(boat) {
  const capacity = {
    plateType: boat.plateType || '',
    calBoatWeight: boat.calBoatWeight === undefined ? '' : boat.calBoatWeight,

    reqMaxNumberOfPersons: boat.reqMaxNumberOfPersons === undefined ? '' : boat.reqMaxNumberOfPersons,
    reqMaxPersonsWeight: boat.reqMaxPersonsWeight === undefined ? '' : boat.reqMaxPersonsWeight,
    reqMaxWeight: boat.reqMaxWeight === undefined ? '' : boat.reqMaxWeight,

    calMaxNumberOfPersons: boat.calMaxNumberOfPersons === undefined ? '' : boat.calMaxNumberOfPersons,
    calMaxPersonsWeight: boat.calMaxPersonsWeight === undefined ? '' : boat.calMaxPersonsWeight,
    calMaxWeight: boat.calMaxWeight === undefined ? '' : boat.calMaxWeight,

    appMaxWeight: boat.appMaxWeight === undefined ? '' : boat.appMaxWeight,
    appMaxNumberOfPersons: boat.appMaxNumberOfPersons === undefined ? '' : boat.appMaxNumberOfPersons,
    appMaxPersonsWeight: boat.appMaxPersonsWeight === undefined ? '' : boat.appMaxPersonsWeight,

    overrideApprovedValues:boat.overrideApprovedValues,
    preferredTagType: boat.preferredTagType,
    useManufacturerEngineWeight: boat.useManufacturerEngineWeight,
  };

  if (this.engineType === OUTBOARD) {
    capacity.reqMaxHP = boat.reqMaxHP === undefined ? '' : boat.reqMaxHP;
    capacity.calcMaxHP = boat.calcMaxHP === undefined ? '' : boat.calcMaxHP;
    capacity.apprMaxHP = boat.apprMaxHP === undefined ? '' : boat.apprMaxHP;
    capacity.reqSpecifiedEngineWeight = boat.reqSpecifiedEngineWeight !== undefined
      ? boat.reqSpecifiedEngineWeight
      : (boat.specifiedEngineWeight === undefined || boat.specifiedEngineWeight === null ? '' : boat.specifiedEngineWeight);
  }

  if (boat.dualSteering) {
    capacity.reqMaxHPDualSteering = boat.reqMaxHPDualSteering || '';
    capacity.calcMaxHPDualSteering = boat.calcMaxHPDualSteering || '';
    capacity.apprMaxHPDualSteering = boat.apprMaxHPDualSteering || '';
  }

  return capacity;
}

function _initPowerCapacity(boat) {
  return {
    maxSpeedLessThan30: boat.maxSpeedLessThan30 !== undefined ? boat.maxSpeedLessThan30 : true,
    passesTestAtFullPower: !!boat.passesTestAtFullPower,
    maneuveringSpeed: boat.maneuveringSpeed === undefined ? '' : boat.maneuveringSpeed
  };
}

function _initHullCalculations(boat) {
  if (!boat.tubes && boat.cubicCapacity.tubes) {
    return {...boat.cubicCapacity};
  } else if (boat.tubes && boat.tubes.length) {
    let newCubicCapacity = {
      calculatedPontoonDiameter: boat.avgOfDiameter,
      calculatedPontoonLength: boat.avgOfTotalLength,
      calculatedTubesVolume: boat.totalVolumeOfAllTubes,
      hasCenterTube: boat.hasCenterTube,
      tubes: {},
      invalidCells: {...initState.cubicCapacity.invalidCells},
    };
    boat.tubes.forEach(tube => {
      newCubicCapacity.tubes[tube.type] = tube;
    });
    return newCubicCapacity;
  }
  return {
    tubes: {
      'PORT': {},
      'CENTER': {},
      'STARBOARD': {},
    },
    invalidCells: {
      'PORT': {},
      'CENTER': {},
      'STARBOARD': {},
    },
    hasCenterTube: false
  }
}

function _initFloatationCapacity(floatation) {
  if (floatation && floatation.length) {
    return {
      testDate: moment(floatation[0].testDate).utc(),
      files: floatation
    };
  } else {
    return {};
  }
}



// WEBPACK FOOTER //
// ./src/app/store/models/Pontoon.js